import React, { useState, useEffect, useContext } from "react";
import "../../App.css";
import DonateForm from "./DonateForm";
import TermsAndConditionsPopup from "./Terms_conditionForm"; // Import the new popup
import { FaTimes, FaFacebook, FaWhatsapp, FaShare } from "react-icons/fa"; // Import icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXTwitter} from "@fortawesome/free-brands-svg-icons"; // Import other brand icons
import { UserContext } from "../context/UserContext"; // Import UserContext
import { BsShare } from 'react-icons/bs'; // Add this import at the top instead of FaShare

const LocalProject = () => {
  const { isLoggedIn } = useContext(UserContext); // Get isLoggedIn state
  const [selectedProjectId, setSelectedProjectId] = useState(
    localStorage.getItem("selectedProjectId") || "60"
  );
  
  const [showPopup, setShowPopup] = useState(false); // For Donate Now popup
  const [showCreatorPopup, setShowCreatorPopup] = useState(false); // For Campaign Creator popup
  const [projectData, setProjectData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [donations, setDonations] = useState([]); // For donation list
  const [totalRaised, setTotalRaised] = useState(0); // Total amount raised
  const [backerCount, setBackerCount] = useState(0); // Number of unique
  const [isSharePopupVisible, setIsSharePopupVisible] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedProjectId = urlParams.get("projectId") || urlParams.get("selectedProjectId");
    const sharedPoliticianId = urlParams.get("politicianId") || urlParams.get("selectedPoliticianId");
    const selectionType = urlParams.get("selectionType");

    // Handle politician selection
    if (sharedPoliticianId && selectionType === "politician") {
      localStorage.setItem("selectedPoliticianId", sharedPoliticianId);
      localStorage.setItem("selectionType", "politician");
      return; // Exit early as politician will be handled by Politician component
    }

    // Handle project/charity selection from URL parameters
    if (sharedProjectId) {
      setSelectedProjectId(sharedProjectId);
      localStorage.setItem("selectedProjectId", sharedProjectId);
      if (selectionType) {
        localStorage.setItem("selectionType", selectionType);
      }
    } else {
      // Only use localStorage if no URL parameters are present
      const storedProjectId = localStorage.getItem("selectedProjectId");
      if (storedProjectId && storedProjectId !== selectedProjectId) {
        setSelectedProjectId(storedProjectId);
      }
    }

    // Add event listener for storage changes
    const handleStorageChange = (e) => {
      if (e.key === "selectedProjectId") {
        const newProjectId = e.newValue;
        if (newProjectId && newProjectId !== selectedProjectId) {
          setSelectedProjectId(newProjectId);
        }
      }
    };

    // Add event listener for immediate updates
    const handleProjectSelected = (e) => {
      const newProjectId = e.detail.projectId;
      if (newProjectId && newProjectId !== selectedProjectId) {
        setSelectedProjectId(newProjectId);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("projectSelected", handleProjectSelected);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("projectSelected", handleProjectSelected);
    };
  }, [selectedProjectId]); // Add selectedProjectId as dependency

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!selectedProjectId) return;

      try {
        setIsLoading(true);

        // Fetch project data
        let projectResponse = await fetch(
          `/api/projectlist/${selectedProjectId}`
        );
        if (!projectResponse.ok) {
          throw new Error("The selected project has been deleted");
        }
        const projectData = await projectResponse.json();

        // Fetch donation data
        const donationResponse = await fetch(
          `/api/project-donations/${selectedProjectId}`
        );
        if (!donationResponse.ok)
          throw new Error("Failed to fetch donation data");
        const donationData = await donationResponse.json();

        // Set state for project details
        setProjectData({
          image: projectData.image_url,
          title: projectData.project_title,
          category: projectData.category,
          description: projectData.description,
          campaignCreator: projectData.email_id,
          targetAmount: projectData.target_amount,
          currency: projectData.currency,
          endDate: new Date(projectData.end_date).toLocaleDateString(),
          projectStatus:
            new Date(projectData.end_date) > new Date()
              ? "Ongoing"
              : "Completed",
          type: projectData.type,
        });

        // Set state for donations
        setDonations(donationData.donations);
        setTotalRaised(donationData.totalRaised);
        setBackerCount(donationData.backerCount);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectData();
  }, [selectedProjectId]);

  if (isLoading) return <div style={{ color: "white" }}>Loading...</div>;
  if (error) return <div style={{ color: "white" }}>Error: {error}</div>;
  if (!projectData)
    return <div style={{ color: "white" }}>No project found</div>;

  const toggleDonatePopup = () => {
    setShowPopup(!showPopup);
  };

  const toggleCreatorPopup = () => {
    setShowCreatorPopup(!showCreatorPopup);
  };

  const handleDonateClick = () => {
    setShowPopup(true);
  };

  const generateShareableLink = () => {
    if (!selectedProjectId) {
      alert("No project selected for sharing.");
      return;
    }

    // Get the current selection type from local storage
    const selectionType = localStorage.getItem("selectionType") || 'project'; // Default to 'project' if not found

    // Construct the shareable URL including the selection type
    const shareUrl = `${window.location.origin}/main/?selectedProjectId=${selectedProjectId}&selectionType=${selectionType}`;

    // Copy to clipboard
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        alert("Link copied to clipboard! Share it with your friends.");
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });

    return shareUrl;
  };

  const shareToSocialMedia = (platform) => {
    const shareUrl = generateShareableLink();
    if (!shareUrl) return;

    const message = `Check out this local project on UrVote!`;
    const encodedUrl = encodeURIComponent(shareUrl);
    let socialUrl = "";

    switch (platform) {
      case "facebook":
        socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case "twitter":
        socialUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          message
        )}&url=${encodedUrl}`;
        break;
      case "whatsapp":
        socialUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
          message
        )}%20${encodedUrl}`;
        break;
      case "email":
        socialUrl = `mailto:?subject=Check out this project&body=${message}%20${encodedUrl}`;
        break;
      default:
        alert("Unsupported platform.");
        return;
    }

    window.open(socialUrl, "_blank");
  };

  return (
    <div style={{
      padding: window.innerWidth <= 926 ? "10px" : "2px",
      borderRadius: window.innerWidth <= 926 ? "0" : "10px",
    }}>
      <div style={{ 
        display: "flex", 
        flexDirection: window.innerWidth <= 926 ? "column" : "row",
        gap: window.innerWidth <= 926 ? "8px" : "10px"
      }}>
        {/* Title Section - Only for Mobile */}
        {window.innerWidth <= 926 && (
          <div style={{
            marginBottom: "8px",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start"
          }}>
            <div style={{ 
              flex: 1,
              textAlign: "left"
            }}>
              <h2 style={{ 
                fontSize: "20px",
                marginBottom: "3px",
                fontWeight: "normal",
                textAlign: "left"
              }}>{projectData.title}</h2>
              <p style={{ 
                fontSize: "14px",
                color: "#cccccc",
                marginBottom: "3px",
                textAlign: "left"
              }}>#{projectData.category}</p>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                marginTop: "3px",
                fontSize: "12px",
                textAlign: "left"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                  whiteSpace: "nowrap"
                }}>
                  <span style={{ textAlign: "left" }}>{backerCount} Backers</span>
                  <span style={{ marginRight: "25px", textAlign: "left" }}>End Date: {projectData.endDate}</span>
                </div>
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "3px",
                  marginLeft: "auto"
                }}>
                  <span style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: projectData.projectStatus === "Ongoing" ? "lightgreen" : "red"
                  }}></span>
                  {projectData.projectStatus === "Ongoing" ? "Live" : "Closed"}
                </span>
              </div>
            </div>
            
            {/* Share Icon Button for Mobile */}
            <button
              onClick={() => setIsSharePopupVisible(true)}
              style={{
                padding: "8px",
                background: "#087cfc",
                color: "white",
                borderRadius: "5px",
                cursor: "pointer",
                height: "fit-content",
                marginLeft: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <BsShare size={16} style={{ 
                strokeWidth: 1, // Makes the icon bolder
                fontWeight: "bold",
                transform: "scale(1.2)" // Slightly larger icon
              }} />
            </button>
          </div>
        )}

        {/* Image Section */}
        <div style={{ 
          width: window.innerWidth <= 926 ? "100%" : "35%",
          marginBottom: window.innerWidth <= 926 ? "5px" : "0",
          display: "flex",
          flexDirection: "column",
          gap: window.innerWidth <= 926 ? "8px" : "10px"
        }}>
          {projectData.image?.match(/\.(jpeg|jpg|png|gif)$/i) ? (
            <img
              src={projectData.image}
              alt={projectData.title}
              style={{
                width: "100%",
                height: window.innerWidth <= 926 ? "200px" : "250px",
                borderRadius: "5px",
                border: "0.1px solid #cccccc",
                objectFit: "cover"
              }}
            />
          ) : projectData.image?.match(/\.(mp4|mov|avi|mkv)$/i) ? (
            <video
              controls
              style={{
                width: "100%",
                height: window.innerWidth <= 926 ? "200px" : "250px",
                borderRadius: "5px",
                border: "0.1px solid #cccccc",
                objectFit: "cover"
              }}
            >
              <source src={projectData.image} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div style={{
              width: "100%",
              height: window.innerWidth <= 926 ? "200px" : "250px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "10px",
              backgroundColor: "#333",
              color: "white"
            }}>
              No media available
            </div>
          )}

          {/* Campaign Creator Button - Desktop Only */}
          {window.innerWidth > 926 && (
            <button
              onClick={toggleCreatorPopup}
              style={{
                width: "100%",
                padding: "12px",
                background: "linear-gradient(to right, #007bff, #00d4ff)",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              Campaign Creator
            </button>
          )}
        </div>

        {/* Content Section - Desktop Only */}
        {window.innerWidth > 926 && (
          <div style={{ flex: "2" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ fontSize: "20px", color: "white" }}>
                {projectData.title}
              </h3>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => setIsSharePopupVisible(true)}
                  style={{
                    padding: "8px 12px",
                    background: "#087cfc",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Share
                </button>
                <button
                  onClick={handleDonateClick}
                  disabled={projectData.projectStatus === "Completed" || !isLoggedIn}
                  style={{
                    padding: "8px 12px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "white",
                    background:
                      projectData.projectStatus === "Completed" || !isLoggedIn
                        ? "#ccc"
                        : "#007bff",
                    border: "none",
                    borderRadius: "5px",
                    cursor:
                      projectData.projectStatus === "Completed" || !isLoggedIn
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {!isLoggedIn ? "Login to Give" : "Give Now"}
                </button>
              </div>
            </div>
            <p style={{ color: "#cccccc", fontSize: "14px" }}>
              #{projectData.category}
              <br />
              {backerCount} Backers
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <p style={{ fontSize: "14px", fontWeight: "bold", color: "white" }}>
              End Date: {projectData.endDate}
              </p>
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {projectData.projectStatus === "Ongoing" ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        backgroundColor: "lightgreen",
                        borderRadius: "50%",
                        marginRight: "6px",
                      }}
                    ></span>
                    <span style={{ color: "white" }}>Live</span>
                  </>
                ) : (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        backgroundColor: "red",
                        borderRadius: "50%",
                        marginRight: "6px",
                      }}
                    ></span>
                    <span style={{ color: "white" }}>Closed</span>
                  </>
                )}
              </p>
            </div>

            <h4
              style={{
                fontSize: "16px",
                color: "white",
                fontWeight: "bold",
              }}
            >
              Description
            </h4>
            <div
              style={{
                border: "1px solid #ccc",
                padding: "10px",
                height: "145px",
                overflowY: "scroll",
                borderRadius: "5px",
                backgroundColor: "white",
                color: "black",
              }}
            >
              {projectData.description.split("\n").map((paragraph, index) => (
                <p
                  key={index}
                  style={{
                    fontSize: "12px",
                    lineHeight: "1.4",
                    marginBottom: "10px",
                  }}
                >
                  {paragraph.trim()}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Campaign Creator Buttons - Mobile Only */}
        {window.innerWidth <= 926 && (
          <div style={{
            display: "flex",
            gap: "8px",
            marginTop: "5px"
          }}>
            <button
              onClick={toggleCreatorPopup}
              style={{
                flex: 1,
                padding: "12px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                fontSize: "14px",
                fontWeight: "bold"
              }}
            >
              Campaign Creator
            </button>
            <button
              onClick={handleDonateClick}
              disabled={projectData.projectStatus === "Completed" || !isLoggedIn}
              style={{
                flex: 1,
                padding: "12px",
                backgroundColor: projectData.projectStatus === "Completed" || !isLoggedIn ? "#ccc" : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: projectData.projectStatus === "Completed" || !isLoggedIn ? "not-allowed" : "pointer"
              }}
            >
              {!isLoggedIn ? "Login to Give" : "Give Now"}
            </button>
          </div>
        )}

        {/* Description Section - Mobile Only */}
        {window.innerWidth <= 926 && (
          <div style={{
            marginTop: "15px"
          }}>
            <h3 style={{
              fontSize: "18px",
              color: "white",
              marginBottom: "8px"
            }}>Description</h3>
            <div style={{
              backgroundColor: "white",
              padding: "15px",
              borderRadius: "10px",
              maxHeight: "200px",
              overflowY: "auto"
            }}>
              {projectData.description.split("\n").map((paragraph, index) => (
                <p key={index} style={{
                  fontSize: "14px",
                  marginBottom: "10px"
                }}>
                  {paragraph.trim()}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "white",
            fontSize: "12px",
            fontWeight: "bold",
            marginTop: "10px"
          }}
        >
          <p>
            Total Raised Amount: {"$"}
            {totalRaised}
          </p>
          <p style={{ marginLeft: "20px" }}>
            Target Amount: {"$"}
            {projectData.targetAmount}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div
        style={{
          backgroundColor: "#e0e0e0",
          borderRadius: "5px",
          height: "10px",
          position: "relative",
        }}
      >
        <div
          style={{
            backgroundColor: "#007bff",
            width: `${Math.min(
              (totalRaised / projectData.targetAmount) * 100,
              100
            )}%`,
            height: "100%",
            borderRadius: "5px",
          }}
        ></div>
      </div>

      {/* Table */}
      <div
        style={{
          marginTop: "5px",
          border: "1px solid black", // Border for the table container
          maxHeight: "23%", // Fixed height for the table
          overflowY: "auto", // Enable vertical scrolling
          backgroundColor: "white", // Ensure a consistent background color
        }}
      >
        <table
          style={{
            width: "100%", // Full width for the table
            borderCollapse: "collapse", // Collapse table borders
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f2f2f2", height: "40px" }}>
              <th style={{ textAlign: "center", padding: "8px" }}>
                Backer Name
              </th>
              <th style={{ textAlign: "center", padding: "8px" }}>
                Donation Amount in AUD
              </th>
              <th style={{ textAlign: "center", padding: "8px" }}>
                Date & Time
              </th>
            </tr>
          </thead>
          <tbody>
            {donations.map((donation, index) => (
              <tr
                key={index}
                style={{
                  borderBottom: "1px solid #ccc",
                }}
              >
                <td style={{ textAlign: "center", padding: "8px" }}>
                  {donation.fullName}
                </td>
                <td style={{ textAlign: "center", padding: "8px" }}>
                  {donation.amount}
                </td>
                <td style={{ textAlign: "center", padding: "8px" }}>
                  {new Date(donation.timestamp).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Donate Form Popup: TODO : Adjust to show the whole box  */}
      {showPopup && (
        <div>
          {projectData.type === "charity" ? (
            // For charity projects, show DonateForm directly
            <DonateForm 
              onClose={toggleDonatePopup}
              selectedPurpose="monthly payment"
              isCharity={true}
              projectData={projectData}
            />
          ) : (
            // For regular projects, show Terms & Conditions first
            <TermsAndConditionsPopup
              projectData={projectData}
              onClose={toggleDonatePopup}
            />
          )}
        </div>
      )}

      {/* Campaign Creator Popup */}
      {showCreatorPopup && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            color: "black",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
          }}
        >
          <p style={{ fontSize: "14px", fontWeight: "bold" }}>
            Campaign Creator Email: {projectData.campaignCreator}
          </p>
          <button
            onClick={toggleCreatorPopup}
            style={{
              marginTop: "10px",
              padding: "8px 12px",
              background: "linear-gradient(to right, #007bff, #00d4ff)",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      )}

      {/* Share Popup */}
      {isSharePopupVisible && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            color: "black",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
          }}
        >
      {/* Close Button (Moved to Top-Right) */}
      <FaTimes
            onClick={() => setIsSharePopupVisible(false)}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              cursor: "pointer",
              color: "black",
              fontSize: "20px",
            }}
          />

          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3>Share this project</h3>
          </div>

          {/* Share Icons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              marginTop: "10px",
            }}
          >
            <FaFacebook
              title="Facebook"
              style={{
                fontSize: "24px",
                color: "#3b5998",
                cursor: "pointer",
              }}
              onClick={() => shareToSocialMedia("facebook")}
            />
            <FontAwesomeIcon
              icon={faXTwitter}
              title="Twitter"
              style={{
                fontSize: "24px",
                color: "#1DA1F2",
                cursor: "pointer",
              }}
              onClick={() => shareToSocialMedia("twitter")}
            />
            <FaWhatsapp
              title="WhatsApp"
              style={{
                fontSize: "24px",
                color: "#25D366",
                cursor: "pointer",
              }}
              onClick={() => shareToSocialMedia("whatsapp")}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalProject;
